import type { PermissionApi } from '@data-delivery/permission-ui-api';
import { type z } from 'zod';
import { type formSchema } from './form-schema';

export interface ChangeSnapshotField {
  key: string;
  label: string;
  before: string | undefined;
  after: string | undefined;
}

export interface ChangeSnapshot {
  fields: ChangeSnapshotField[];
  productsAdded: string[];
  productsRemoved: string[];
  servicesAdded: string[];
  servicesRemoved: string[];
  changedCount: number;
  businessGroupChanged: boolean;
  entityMaps?: {
    businessGroups?: Map<string, { name?: string; shortName?: string }>;
    products?: Map<string, { name: string }>;
  };
}

export function buildChangeSnapshot(
  initial: PermissionApi.AssignmentEntity | undefined,
  values: z.infer<typeof formSchema>,
  entityMaps?: {
    businessGroups?: PermissionApi.BusinessGroupEntity[];
    products?: PermissionApi.ProductEntity[];
  },
): ChangeSnapshot | undefined {
  if (!initial) {
    return undefined;
  }

  const normalizeDate = (d: number | string | undefined) => {
    if (d === '' || d == null) {
      return '';
    }

    if (typeof d === 'number') {
      return new Date(d).toISOString().slice(0, 10);
    }

    return d;
  };

  const initialStart = normalizeDate(initial.startDate);
  const initialEnd = normalizeDate(initial.endDate);
  const nextStart = normalizeDate(values.startDate);
  const nextEndRaw = values.endDate === '' ? '' : normalizeDate(values.endDate);

  const initialProducts = initial.products || [];
  const nextProducts = values.products || [];
  const initialServices = initial.services || [];
  const nextServices = values.services || [];

  const productsAdded = nextProducts.filter(p => !initialProducts.includes(p));
  const productsRemoved = initialProducts.filter(p => !nextProducts.includes(p));
  const servicesAdded = nextServices.filter(s => !initialServices.includes(s));
  const servicesRemoved = initialServices.filter(s => !nextServices.includes(s));

  const fieldDefs: ChangeSnapshotField[] = [
    { key: 'name', label: 'Name', before: initial.name, after: values.name },
    {
      key: 'description',
      label: 'Description',
      before: initial.description,
      after: values.description,
    },
    { key: 'contract', label: 'Contract', before: initial.contract, after: values.contract },
    {
      key: 'businessGroup',
      label: 'Business Group',
      before: initial.businessGroup,
      after: values.businessGroup,
    },
    { key: 'startDate', label: 'Start Date', before: initialStart, after: nextStart },
    { key: 'endDate', label: 'End Date', before: initialEnd, after: nextEndRaw },
  ];

  const fieldsChanged = fieldDefs.filter(f => (f.before || '') !== (f.after || ''));
  const arraysChanged = [
    productsAdded.length > 0,
    productsRemoved.length > 0,
    servicesAdded.length > 0,
    servicesRemoved.length > 0,
  ].filter(Boolean).length;

  const changedCount = fieldsChanged.length + arraysChanged;

  const businessGroupsMap = entityMaps?.businessGroups
    ? new Map(
        entityMaps.businessGroups.map(bg => [
          bg.id,
          {
            name: (bg as { name?: string }).name,
            shortName: bg.shortName,
          },
        ]),
      )
    : undefined;

  const productsMap = entityMaps?.products
    ? new Map(entityMaps.products.map(p => [p.id, { name: p.name }]))
    : undefined;

  const businessGroupChanged = initial.businessGroup !== values.businessGroup;

  return {
    fields: fieldsChanged,
    productsAdded,
    productsRemoved,
    servicesAdded,
    servicesRemoved,
    changedCount,
    businessGroupChanged,
    entityMaps: {
      businessGroups: businessGroupsMap,
      products: productsMap,
    },
  };
}
